export function isPagoWayletAlbaran(albaran: {
  AlbaranFOPs?: { FOP?: string; TipoDispositivo?: string }[];
  PagoCCInfo?: { TipoDispositivo?: string; TipoTarjeta?: string };
} | null | undefined): boolean {
  if (!albaran) {
    return false;
  }
  const wayletEnFops = albaran.AlbaranFOPs?.some(
    (fop) => fop.FOP === 'CC' && fop.TipoDispositivo === 'WAYLET'
  );
  if (wayletEnFops) {
    return true;
  }
  const pagoCC = albaran.PagoCCInfo;
  return (
    pagoCC?.TipoDispositivo === 'WAYLET' || pagoCC?.TipoTarjeta === 'WAYLET'
  );
}

export function isWayletFormaPagoFop(
  fop: { FOP?: string; TipoDispositivo?: string } | null | undefined,
  albaran: Parameters<typeof isPagoWayletAlbaran>[0]
): boolean {
  if (!fop || fop.FOP !== 'CC') {
    return false;
  }
  if (fop.TipoDispositivo === 'WAYLET') {
    return true;
  }
  return isPagoWayletAlbaran(albaran);
}

export function resolveCcTipoDispositivo(
  albaran: Parameters<typeof isPagoWayletAlbaran>[0],
  fop?: { FOP?: string; TipoDispositivo?: string } | null,
  options?: { forceWaylet?: boolean }
): string {
  if (options?.forceWaylet || isWayletFormaPagoFop(fop ?? { FOP: 'CC' }, albaran)) {
    return 'WAYLET';
  }
  return '';
}

export function buildCcMedioPago(
  importe: number | string,
  albaran: Parameters<typeof isPagoWayletAlbaran>[0],
  fop?: { FOP?: string; TipoDispositivo?: string } | null,
  options?: { forceWaylet?: boolean; extra?: Record<string, unknown> }
): Record<string, unknown> {
  const importeValue =
    typeof importe === 'number' ? importe.toFixed(2) : String(importe);
  return {
    FOP: 'CC',
    Importe: importeValue,
    TipoDispositivo: resolveCcTipoDispositivo(albaran, fop, options),
    ...(options?.extra || {}),
  };
}

export function getWayletPaymentIdOrigen(pagoCCInfo: {
  DatosAdicionales?: string;
} | null | undefined): string {
  return (pagoCCInfo?.DatosAdicionales || '').trim();
}

export function parseImporteCcAlbaran(albaran: {
  AlbaranFOPs?: { FOP?: string; Importe?: string }[];
}): number {
  const fopCc = albaran.AlbaranFOPs?.find((f) => f.FOP === 'CC');
  if (!fopCc?.Importe) {
    return 0;
  }
  return Number.parseFloat(String(fopCc.Importe).replace(',', '.')) || 0;
}

export interface BuildOperacionesTarjetaWayletParams {
  tipoOperacion: '01' | '02';
  importe: number;
  divisaId: string | number;
  identificadorOrigen?: string;
  wayletPaymentId?: string;
  paymentId?: string;
}

/** Mapeo wiki Waylet para OperacionesTarjeta (venta 01 / abono 02). */
export function buildOperacionesTarjetaWaylet(
  params: BuildOperacionesTarjetaWayletParams
): Record<string, unknown> {
  const esVenta = params.tipoOperacion === '01';
  return {
    FechaHora: '',
    Importe: params.importe,
    TipoOperacion: params.tipoOperacion,
    NumAutorizacion2oBloque: '',
    CodigoAceptacion: 'AC',
    TextoInformativo: '',
    TextoReservadoControlador: esVenta ? params.wayletPaymentId || '' : '',
    NumTarjeta: '',
    FechaCaducidad: '',
    NumAutorizacion1erBloque: '',
    FechaConciliacion1erBloque: '',
    FechaConciliacion2oBloque: '',
    TieneLector: '0',
    LecturaElectronica: '0',
    PresenciaTitular: '1',
    TarjetaPresente: '1',
    TipoTarjeta: 'WAYLET',
    NumOperacion: 0,
    IdentEstablecimiento2oBloque: '',
    DatosAdicionales: params.paymentId || '',
    DatosAdicionalesPrivados: '',
    CodigoAccion: '',
    PedidoBase: '',
    CodigoClienteID: '',
    NumReimpresiones: '1',
    IntroduccionManual: '1',
    DivisaId: params.divisaId ?? '',
    ...(params.identificadorOrigen
      ? { Identificador: params.identificadorOrigen }
      : {}),
  };
}

export function buildOperacionesTarjetaWayletAbono(
  albaran: {
    NumVenta?: string;
    PagoCCInfo?: { DatosAdicionales?: string };
    DivisaId?: string | number;
  },
  importe: number
): Record<string, unknown> {
  const paymentId = getWayletPaymentIdOrigen(albaran.PagoCCInfo);
  return buildOperacionesTarjetaWaylet({
    tipoOperacion: '02',
    importe,
    divisaId: albaran.DivisaId ?? '',
    identificadorOrigen: albaran.NumVenta || '',
    paymentId,
  });
}
